import React, { useMemo } from 'react';
import { get } from 'lodash';
import { useQuery } from '@apollo/client';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { getFilterOptionsForInvoiceGroupsQuery } from '@css-xp/ppc-utils';
import { maxThresholdForResults } from '@css-xp/ppc-constants';
import { OrderHistoryTable } from '@css-xp/ppc-order-history-table';
import { fetchInvoiceGroupsForSearch } from './queries/fetch-invoice-groups-for-search';
import { fetchEntitlementIdsForEndCustomerContact } from './queries/fetch-entitlement-ids-for-ecc';
import {
  PartnerSearchContext,
  FetchEntitlementIdsForEndCustomerContactQuery,
  FetchEntitlementIdsForEndCustomerContactQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';

interface OrdersSearchProps {
  searchTerm: string;
  transactionAccountId: string;
  directMatchEntity?: string;
  isDesignIteration1Enabled?: boolean;
}

const fetchPolicy = 'cache-and-network';

export const OrdersSearch = ({
  searchTerm,
  transactionAccountId,
  directMatchEntity,
  isDesignIteration1Enabled,
}: OrdersSearchProps) => {
  const searchOptionsForDependentQueries = useMemo(() => {
    return {
      first: maxThresholdForResults,
      offset: 0,
    };
  }, []);

  // Currently, there is no direct access of sold-to-address entity at Order level.
  // So, we first fetch all the invoice groups based on `shipToName`, `shipToAddress1` and `shipToAddress2`, then
  // fetch all the orders based on the invoice group ids (`billingGroupId`).
  const { data, loading, error } = useQuery<
    generatedTypesNew.FetchInvoiceGroupsForPpcSearchQuery,
    generatedTypesNew.FetchInvoiceGroupsForPpcSearchQueryVariables
  >(fetchInvoiceGroupsForSearch, {
    variables: {
      transactionAccountId,
      searchOptions: searchOptionsForDependentQueries,
      filterOptions: getFilterOptionsForInvoiceGroupsQuery(transactionAccountId, searchTerm),
      isOrgSearchEnabled: true,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: fetchPolicy,
    nextFetchPolicy: 'cache-first',
    skip: !transactionAccountId || directMatchEntity === 'PERSON',
  });

  const {
    data: endCustomerContactData,
    loading: endCustomerContactDataLoading,
    error: endCustomerContactDataError,
  } = useQuery<FetchEntitlementIdsForEndCustomerContactQuery, FetchEntitlementIdsForEndCustomerContactQueryVariables>(
    fetchEntitlementIdsForEndCustomerContact,
    {
      variables: {
        searchParams: {
          query: searchTerm,
          transactionAccountId,
          partnerSearchContext: PartnerSearchContext.PERSON,
        },
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: fetchPolicy,
      nextFetchPolicy: 'cache-first',
      skip: !transactionAccountId || directMatchEntity !== 'PERSON',
    }
  );

  const invoiceGroups = data?.invoiceGroups?.nodes ?? [];

  if (
    directMatchEntity !== 'PERSON' &&
    ((!invoiceGroups?.length && !loading) || error || endCustomerContactDataLoading || endCustomerContactDataError)
  ) {
    return (
      <OrderHistoryTable
        filters={[]}
        transactionAccountId=""
        isSearchHidden={true}
        error={error ?? endCustomerContactDataError}
        isExportingCSVHidden={true}
        shouldHideFilters={isDesignIteration1Enabled}
        searchTerm={searchTerm}
      />
    );
  }

  const orFilter = invoiceGroups.map((invoiceGroup: generatedTypesNew.InvoiceGroup | null) => {
    return {
      key: 'billingGroupId',
      value: invoiceGroup?.id,
    };
  });
  let andfilter = get(endCustomerContactData, 'endCustomerContacts.nodes[0].entitlements.entitlementIds', []);
  if (andfilter && andfilter?.length > 200) {
    andfilter = andfilter?.slice(0, 200);
  }
  if (directMatchEntity === 'PERSON' && !andfilter?.length) {
    return (
      <OrderHistoryTable
        filters={[]}
        transactionAccountId=""
        isSearchHidden={true}
        error={error ?? endCustomerContactDataError}
        isExportingCSVHidden={true}
        shouldHideFilters={isDesignIteration1Enabled}
        searchTerm={searchTerm}
      />
    );
  }
  const filters =
    directMatchEntity === 'PERSON'
      ? [
          {
            and: [
              {
                or: andfilter?.map((entitlementId: string) => {
                  return {
                    key: 'orderLineItemsEntitlementId',
                    value: entitlementId as string,
                  };
                }),
              },
            ],
          },
        ]
      : [
          {
            and: [
              {
                or: orFilter,
              },
            ],
          },
        ];

  return (
    <div data-testid="orders-list">
      <OrderHistoryTable
        filters={filters}
        transactionAccountId={transactionAccountId}
        isSearchHidden
        isExportingCSVHidden={true}
        shouldHideFilters={isDesignIteration1Enabled}
        searchTerm={searchTerm}
      />
    </div>
  );
};

OrdersSearch.displayName = 'OrdersSearch';
